import {
  type ChainflipAsset,
  chainflipAssetToPriceAssetMap,
  type InternalAssetMap,
  isLegacyChainflipAsset,
} from '@chainflip/utils/chainflip';
import { getOraclePricesQuery } from '@/shared/graphql/cache/assets';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';

export type OraclePriceData = InternalAssetMap<{
  usdPrice: number;
  isStale: boolean;
  lastUpdated: Date;
}>;

// oracle price refer to the chainlink oracle price of the asset tracked on the state chain
export const useOraclePrices = () => {
  const { data } = useGqlQuery(getOraclePricesQuery, {
    context: { clientName: 'statechainCache' },
    refetchInterval: 6_000,
  });

  if (!data?.prices?.nodes) return undefined;

  const chainflipAssetToPriceAssetArrays = Object.entries(chainflipAssetToPriceAssetMap);

  return data.prices.nodes.reduce((acc, { baseAsset, price, status, updatedAtOracleTimestamp }) => {
    for (const [cfAsset, priceAsset] of chainflipAssetToPriceAssetArrays) {
      if (priceAsset === baseAsset) {
        acc[cfAsset as ChainflipAsset] = {
          usdPrice: price,
          isStale: status === 'Stale',
          lastUpdated: new Date(updatedAtOracleTimestamp),
        };
      }
    }
    return acc;
  }, {} as OraclePriceData);
};

export const useOraclePrice = (asset: ChainflipAsset | undefined) => {
  const prices = useOraclePrices();
  if (!asset || !prices || isLegacyChainflipAsset(asset)) return undefined;

  return prices[asset]?.usdPrice;
};
